// src/utils/tracking.js

/**
 * Extracts a cookie value by its name.
 * @param {string} name The name of the cookie.
 * @returns {string|undefined} The cookie value or undefined if not found.
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop().split(';').shift();
  }
};

/**
 * Sets a cookie with specified name, value and expiration days.
 * @param {string} name The name of the cookie.
 * @param {string} value The value of the cookie.
 * @param {number} days Number of days until expiration.
 */
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
};

/**
 * Sets a session cookie (expires when browser closes).
 * @param {string} name The name of the cookie.
 * @param {string} value The value of the cookie.
 */
const setSessionCookie = (name, value) => {
  document.cookie = `${name}=${value}; path=/; SameSite=Lax`;
};

/**
 * Extracts UTM parameters from the current URL.
 * @returns {Object} An object containing UTM parameters.
 */
const getUTMParameters = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {};
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  utmKeys.forEach(param => {
    if (urlParams.has(param)) {
      utmParams[param] = urlParams.get(param);
    }
  });

  return utmParams;
};

/**
 * Gets GTM user_id from dataLayer.
 * @returns {string|null} The GTM user_id or null if not found.
 */
const getGTMUserId = () => {
  if (typeof window !== 'undefined' && window.dataLayer && Array.isArray(window.dataLayer)) {
    // Ищем user_id в dataLayer (проверяем последние записи)
    for (let i = window.dataLayer.length - 1; i >= 0; i--) {
      if (window.dataLayer[i].user_id) {
        return window.dataLayer[i].user_id;
      }
    }
  }
  return null;
};

/**
 * Initializes tracking by collecting and storing marketing data in localStorage.
 */
export const initTracking = () => {
  // GCLID is often set by Google Ads and might not be in the URL.
  // In a non-GTM setup, we'll primarily rely on URL parameters or other scripts that place it.
  // For now, we'll attempt to get it from the URL like other params.
  const urlParams = new URLSearchParams(window.location.search);
  const gclid = urlParams.get('gclid');
  const fbclid = urlParams.get('fbclid');

  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  const referrer = document.referrer;
  const utmParams = getUTMParameters();
  const userId = getGTMUserId();

  const hasDataToStore = gclid || fbclid || fbc || fbp || Object.keys(utmParams).length > 0 || referrer || userId;
  const isNotInternalReferrer = !referrer || !referrer.includes(window.location.hostname);

  if (hasDataToStore && isNotInternalReferrer) {
    const gclids = JSON.parse(localStorage.getItem('gclids')) || [];
    const fbclids = JSON.parse(localStorage.getItem('fbclids')) || [];
    const referrers = JSON.parse(localStorage.getItem('referrerUrls')) || [];
    const utmStorage = JSON.parse(localStorage.getItem('utmParams')) || [];

    const storageLimit = 10;
    if (gclids.length >= storageLimit) {
      gclids.shift();
      fbclids.shift();
      referrers.shift();
      utmStorage.shift();
    }

    gclids.push(gclid || '');
    fbclids.push(fbclid || '');
    referrers.push(referrer || '');
    utmStorage.push(Object.keys(utmParams).length > 0 ? utmParams : {});

    // Сохраняем в localStorage (основной метод)
    localStorage.setItem('gclids', JSON.stringify(gclids));
    localStorage.setItem('fbclids', JSON.stringify(fbclids));
    localStorage.setItem('referrerUrls', JSON.stringify(referrers));
    localStorage.setItem('utmParams', JSON.stringify(utmStorage));

    if (fbc) {
      localStorage.setItem('fbc', fbc);
    }
    if (fbp) {
      localStorage.setItem('fbp', fbp);
    }

    // Дублируем данные в cookies для надежности
    try {
      setCookie('gclids', JSON.stringify(gclids), 365);
      setCookie('fbclids', JSON.stringify(fbclids), 365);
      setCookie('referrerUrls', JSON.stringify(referrers), 365);
      setCookie('utmParams', JSON.stringify(utmStorage), 365);
      
      if (fbc) setCookie('fbc', fbc, 365);
      if (fbp) setCookie('fbp', fbp, 365);
      if (userId) setCookie('userId', userId, 365);
    } catch (e) {
      console.warn('Failed to save to cookies:', e);
    }

    // Дублируем данные в session cookies как подстраховку
    try {
      setSessionCookie('gclids_session', JSON.stringify(gclids));
      setSessionCookie('fbclids_session', JSON.stringify(fbclids));
      setSessionCookie('referrerUrls_session', JSON.stringify(referrers));
      setSessionCookie('utmParams_session', JSON.stringify(utmStorage));
      
      if (fbc) setSessionCookie('fbc_session', fbc);
      if (fbp) setSessionCookie('fbp_session', fbp);
      if (userId) setSessionCookie('userId_session', userId);
    } catch (e) {
      console.warn('Failed to save to session cookies:', e);
    }
  }
};

/**
 * Retrieves tracking data from multiple storage methods with fallback logic.
 * @returns {Object} The tracking data object.
 */
const getTrackingData = () => {
  // Пытаемся получить данные из разных источников
  let data = null;
  
  // 1. Пробуем localStorage
  try {
    const gclids = JSON.parse(localStorage.getItem('gclids') || '[]');
    const fbclids = JSON.parse(localStorage.getItem('fbclids') || '[]');
    const referrers = JSON.parse(localStorage.getItem('referrerUrls') || '[]');
    const utmParams = JSON.parse(localStorage.getItem('utmParams') || '[]');
    const fbc = localStorage.getItem('fbc') || '';
    const fbp = localStorage.getItem('fbp') || '';
    
    if (gclids.length > 0 || fbclids.length > 0 || referrers.length > 0 || utmParams.length > 0 || fbc || fbp) {
      data = { gclids, fbclids, referrerUrls: referrers, utmParams, fbc, fbp };
    }
  } catch (e) {
    console.warn('localStorage read failed:', e);
  }
  
  // 2. Если localStorage недоступен, пробуем cookie
  if (!data) {
    try {
      const gclids = JSON.parse(getCookie('gclids') || '[]');
      const fbclids = JSON.parse(getCookie('fbclids') || '[]');
      const referrers = JSON.parse(getCookie('referrerUrls') || '[]');
      const utmParams = JSON.parse(getCookie('utmParams') || '[]');
      const fbc = getCookie('fbc') || '';
      const fbp = getCookie('fbp') || '';
      
      if (gclids.length > 0 || fbclids.length > 0 || referrers.length > 0 || utmParams.length > 0 || fbc || fbp) {
        data = { gclids, fbclids, referrerUrls: referrers, utmParams, fbc, fbp };
      }
    } catch (e) {
      console.warn('Cookie read failed:', e);
    }
  }
  
  // 3. Если и cookie недоступен, пробуем session cookie
  if (!data) {
    try {
      const gclids = JSON.parse(getCookie('gclids_session') || '[]');
      const fbclids = JSON.parse(getCookie('fbclids_session') || '[]');
      const referrers = JSON.parse(getCookie('referrerUrls_session') || '[]');
      const utmParams = JSON.parse(getCookie('utmParams_session') || '[]');
      const fbc = getCookie('fbc_session') || '';
      const fbp = getCookie('fbp_session') || '';
      
      if (gclids.length > 0 || fbclids.length > 0 || referrers.length > 0 || utmParams.length > 0 || fbc || fbp) {
        data = { gclids, fbclids, referrerUrls: referrers, utmParams, fbc, fbp };
      }
    } catch (e) {
      console.warn('Session cookie read failed:', e);
    }
  }
  
  // Возвращаем данные или пустой объект
  return data || {
    gclids: [],
    fbclids: [],
    referrerUrls: [],
    utmParams: [],
    fbc: '',
    fbp: ''
  };
};

/**
 * Gets the current user ID from GTM or cookies.
 * @returns {string|null} The user ID.
 */
export const getUserId = () => {
  // Сначала пробуем получить из GTM
  const gtmUserId = getGTMUserId();
  if (gtmUserId) {
    return gtmUserId;
  }
  
  // Если GTM недоступен, пробуем получить из cookies
  try {
    return getCookie('userId') || getCookie('userId_session') || null;
  } catch (e) {
    return null;
  }
};

/**
 * Cleans URL from marketing parameters for Facebook events.
 * Removes: _gl, gclid, gbraid, fbclid, utm_*, and other marketing parameters.
 * Keeps: pathname + hash (for color variants).
 * @param {string|URL} url - The URL to clean (can be string or URL object).
 * @returns {string} Cleaned URL without marketing parameters.
 */
export const cleanUrlForFacebook = (url) => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    // Если передан объект URL, используем его, иначе создаем новый
    const urlObj = url instanceof URL ? url : new URL(url, window.location.origin);
    
    // Список маркетинговых параметров для удаления
    const marketingParams = [
      '_gl',           // Google Linker
      'gclid',         // Google Click ID
      'gbraid',        // Google Browser ID
      'fbclid',        // Facebook Click ID
      'wbraid',        // WhatsApp Browser ID
      'msclkid',       // Microsoft Click ID
      'twclid',        // Twitter Click ID
      'li_fat_id',     // LinkedIn
      'ttclid',        // TikTok Click ID
      'igshid',        // Instagram Share ID
      'ref',           // Referral
      'ref_src',       // Referral Source
      'ref_url',       // Referral URL
    ];

    // Удаляем все UTM параметры
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'utm_cid'];
    
    // Удаляем маркетинговые параметры
    marketingParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });
    
    // Удаляем UTM параметры
    utmParams.forEach(param => {
      urlObj.searchParams.delete(param);
    });

    // Собираем очищенный URL: pathname + hash + оставшиеся query параметры (если есть)
    let cleanUrl = urlObj.pathname;
    
    // Добавляем hash если есть (важно для расцветок товаров)
    if (urlObj.hash) {
      cleanUrl += urlObj.hash;
    }
    
    // Добавляем оставшиеся query параметры только если они есть и не являются маркетинговыми
    const remainingParams = Array.from(urlObj.searchParams.entries());
    if (remainingParams.length > 0) {
      // Фильтруем только безопасные параметры (например, для внутренней навигации)
      const safeParams = remainingParams.filter(([key]) => {
        // Разрешаем только параметры, которые не являются маркетинговыми
        const lowerKey = key.toLowerCase();
        return !marketingParams.includes(lowerKey) && 
               !utmParams.includes(lowerKey) &&
               !lowerKey.startsWith('utm_') &&
               !lowerKey.startsWith('_gl');
      });
      
      if (safeParams.length > 0) {
        const searchParams = new URLSearchParams();
        safeParams.forEach(([key, value]) => {
          searchParams.append(key, value);
        });
        cleanUrl += `?${searchParams.toString()}`;
      }
    }

    return cleanUrl;
  } catch (error) {
    console.warn('Failed to clean URL for Facebook:', error);
    // Fallback: возвращаем только pathname + hash если передан объект URL
    if (url instanceof URL) {
      return url.pathname + (url.hash || '');
    }
    // Если передан string, пытаемся извлечь pathname
    try {
      const urlObj = new URL(url, window.location.origin);
      return urlObj.pathname + (urlObj.hash || '');
    } catch (e) {
      return '';
    }
  }
};

/**
 * Формирует URL товара для Facebook Pixel событий.
 * @param {Object} item - Объект товара с полями url и article.
 * @returns {string} URL товара в формате /{url}/#{article} или /{url}/ если нет article.
 */
export const getProductUrl = (item) => {
  if (!item || !item.url) {
    return '';
  }
  
  const baseUrl = `/${item.url}/`;
  
  // Если есть article, добавляем хеш
  if (item.article) {
    return `${baseUrl}#${item.article}`;
  }
  
  return baseUrl;
};

export { getTrackingData };

